// JsSIP loaded via <script> tag to avoid Vite/Rollup CJS interop issues
declare global {
  interface Window { JsSIP: any }
}
import type { BrowserPhoneConfig, SipSnapshot, WebRtcMediaQuality } from './types';
import { audioDeviceManager } from './AudioDeviceManager';
import { announcedBuildTarget } from '../WebBuildUpdate';
import type {
  JsSipRuntime,
  SipRegistrationSignal,
  SipSessionEvent,
} from './sip/contracts';
import { createBrowserSipPorts } from './sip/BrowserPorts';
import { SipMediaController } from './sip/SipMediaController';
import { type ConfiguredBrowserPhoneConfig, SipRegistrationController } from './sip/SipRegistrationController';
import { SipSessionController } from './sip/SipSessionController';
import { SipTabLease } from './sip/SipTabLease';
import { WebBuildPhoneGate } from './sip/WebBuildPhoneGate';
import { SessionWebRtcCompatibility } from './sip/WebRtcCompatibility';

const JsSIP = (typeof window !== 'undefined' ? (window as any).JsSIP : null) as JsSipRuntime;

export type { SipSessionEvent } from './sip/contracts';

type SipManagerEventMap = {
  snapshot: SipSnapshot;
  callActivity: { active: boolean };
  sessionNew: SipSessionEvent;
  sessionAccepted: SipSessionEvent;
  sessionEnded: SipSessionEvent;
  sessionFailed: SipSessionEvent;
  registration: { registered: boolean; reason?: string };
  mediaQuality: WebRtcMediaQuality;
};

type Listener<K extends keyof SipManagerEventMap> = (payload: SipManagerEventMap[K]) => void;
type DialOptions = { extraHeaders?: string[] };

const EMPTY_SNAPSHOT: SipSnapshot = {
  state: 'OFFLINE', connected: false, registered: false,
  muted: false, held: false, primaryTab: false,
  sessionDirection: null, remoteNumber: '', error: '',
  startedAt: null, endedAt: null,
};

export class SipManager {
  private static instance: SipManager | null = null;

  static getInstance(): SipManager {
    SipManager.instance ||= new SipManager();
    return SipManager.instance;
  }

  private listeners = new Map<keyof SipManagerEventMap, Set<(payload: any) => void>>();
  private config: BrowserPhoneConfig | null = null;
  private pendingConfig: BrowserPhoneConfig | null = null;
  private snapshot: SipSnapshot = { ...EMPTY_SNAPSHOT };
  private destroyed = false;
  private readonly webBuildGate: WebBuildPhoneGate;
  private readonly tabLease: SipTabLease;
  private readonly media: SipMediaController;
  private readonly registration: SipRegistrationController;
  private readonly session: SipSessionController;

  private constructor() {
    const ports = createBrowserSipPorts();
    this.media = new SipMediaController({
      audioDevices: audioDeviceManager,
      timers: ports.timers,
      now: ports.now,
      createPeerConnection: ports.createPeerConnection,
      createMediaStream: ports.createMediaStream,
      onActivityChange: () => this.emitActivity(),
      onQuality: quality => this.emit('mediaQuality', quality),
      onWarning: message => this.setSnapshot({ error: message }),
      logger: console,
    });
    this.session = new SipSessionController({
      timers: ports.timers,
      now: ports.now,
      logger: console,
      getConfig: () => {
        const config = this.config;
        return config?.configured && config.account
          ? config as ConfiguredBrowserPhoneConfig
          : null;
      },
      isPrimaryTab: () => this.snapshot.primaryTab,
      isRegistered: () => Boolean(
        this.registration.hasUa() && this.snapshot.registered,
      ),
      placeCall: (target, options) => this.registration.call(target, options),
      media: this.media,
      createCompatibility: (session, direction, isCurrent) => new SessionWebRtcCompatibility({
        session, direction, isCurrent, timers: ports.timers, logger: console,
      }),
      onSnapshot: patch => this.setSnapshot(patch),
      onSessionEvent: (name, payload) => this.emit(name, payload),
      onActivityChange: () => this.emitActivity(),
    });
    this.registration = new SipRegistrationController({
      runtime: JsSIP,
      timers: ports.timers,
      now: ports.now,
      logger: console,
      onSignal: signal => this.handleRegistrationSignal(signal),
      onSession: (session, direction) => this.handleRegistrationSession(session, direction),
    });
    this.webBuildGate = new WebBuildPhoneGate({
      storage: ports.sharedStorage,
      events: ports.events,
      decodeTarget: value => announcedBuildTarget(value) || '',
      onPendingChange: change => this.handleWebBuildPendingChange(change),
    });
    this.tabLease = new SipTabLease({
      storage: ports.sharedStorage,
      sessionStorage: ports.tabStorage,
      events: ports.events,
      timers: ports.timers,
      now: ports.now,
      createId: ports.createId,
      canAcquire: () => !this.webBuildGate.isPending(),
      onAcquired: () => this.handleLeaseAcquired(),
      onLost: () => this.handleLeaseLost(),
      onRestore: () => this.handlePageRestore(),
    });
  }

  on<K extends keyof SipManagerEventMap>(
    event: K,
    listener: Listener<K>,
  ): () => void {
    const set = this.listeners.get(event) || new Set();
    set.add(listener as (payload: any) => void);
    this.listeners.set(event, set);
    return () => { set.delete(listener as (payload: any) => void); };
  }

  private emit<K extends keyof SipManagerEventMap>(
    event: K,
    payload: SipManagerEventMap[K],
  ): void {
    this.listeners.get(event)?.forEach(listener => {
      try { listener(payload); } catch (error) { console.error('[telephony] listener failed', error); }
    });
  }

  private emitActivity(): void {
    const active = this.hasLiveCallActivity();
    this.emit('callActivity', { active });
    if (!active && this.pendingConfig) {
      const pending = this.pendingConfig;
      this.pendingConfig = null;
      void this.applyConfiguration(pending).catch(error => {
        this.setSnapshot({
          state: 'ERROR',
          error: error instanceof Error ? error.message : 'Не удалось применить обновлённую SIP-конфигурацию',
        });
      });
    }
  }

  getSnapshot(): SipSnapshot { return { ...this.snapshot }; }
  setWebBuildId(buildId: string): void { this.webBuildGate.setBuildId(buildId); }
  markWebBuildStale(targetBuildId: string): boolean { return this.webBuildGate.markStale(targetBuildId); }
  confirmWebBuildCurrent(targetBuildId: string): boolean { return this.webBuildGate.confirmCurrent(targetBuildId); }
  isWebBuildUpdatePending(): boolean { return this.webBuildGate.isPending(); }

  hasLiveCallActivity(): boolean {
    return Boolean(
      this.session.hasLiveActivity()
      || this.media.hasActivity()
      || ['RINGING', 'DIALING', 'ACTIVE', 'HOLD'].includes(this.snapshot.state),
    );
  }

  private handleWebBuildPendingChange(change: {
    pending: boolean;
    previous: boolean;
  }): void {
    if (change.pending) {
      this.tabLease.startPassiveRetry();
      return;
    }
    if (change.previous && this.snapshot.state === 'PASSIVE' && this.config) {
      this.tabLease.startPassiveRetry();
    }
  }

  private handleLeaseAcquired(): void {
    if (!this.config || this.snapshot.state !== 'PASSIVE') return;
    const config = this.config;
    void this.configure(config).catch(error => {
      this.setSnapshot({
        state: 'ERROR',
        primaryTab: false,
        connected: false,
        registered: false,
        error: error instanceof Error ? error.message : 'Не удалось восстановить телефон',
      });
    });
  }

  private handleLeaseLost(): void {
    void this.stop(false).finally(() => {
      this.setSnapshot({
        primaryTab: false,
        connected: false,
        registered: false,
        state: 'PASSIVE',
        error: 'Телефон активирован в другом окне. Можно вернуть его кнопкой ниже.',
      });
      this.tabLease.startPassiveRetry();
    });
  }

  private handlePageRestore(): void {
    if (!this.config) return;
    void this.configure(this.config).catch(() => undefined);
  }

  getMediaQuality(): WebRtcMediaQuality { return this.media.getMediaQuality(); }
  bindAudioElement(element: HTMLAudioElement | null): void { this.media.bindAudioElement(element); }

  async configure(config: BrowserPhoneConfig): Promise<void> {
    this.webBuildGate.sync();
    if (this.webBuildGate.isPending()) return;
    if (this.hasLiveCallActivity()) {
      this.pendingConfig = config;
      return;
    }
    this.pendingConfig = null;
    await this.applyConfiguration(config);
  }

  private async applyConfiguration(config: BrowserPhoneConfig): Promise<void> {
    // stop() завершает ресурсы синхронно. Здесь нельзя отдавать управление,
    // иначе старая настройка может продолжиться уже после входа другого сотрудника.
    void this.stop(false);
    if (this.webBuildGate.isPending()) return;
    this.destroyed = false;
    this.config = config;

    if (!config.configured || !config.account) {
      this.setSnapshot({ state: 'OFFLINE', error: config.unavailableReason || 'Телефон не настроен' });
      return;
    }

    this.tabLease.configureOwner(config.account.id || config.account.extension);

    if (!this.tabLease.claim()) {
      this.setSnapshot({
        state: 'PASSIVE',
        primaryTab: false,
        connected: false,
        registered: false,
        error: 'Проверяем активность телефона. Подключение восстановится автоматически.',
      });
      this.tabLease.startPassiveRetry();
      return;
    }

    this.setSnapshot({ state: 'CONNECTING', primaryTab: true, error: '' });
    this.tabLease.startHeartbeat();

    try {
      this.registration.start(config as ConfiguredBrowserPhoneConfig);
    } catch (error) {
      this.setSnapshot({ state: 'ERROR', error: error instanceof Error ? error.message : 'Не удалось запустить SIP-клиент' });
      throw error;
    }
  }

  private handleRegistrationSignal(signal: SipRegistrationSignal): void {
    if (this.destroyed) return;
    if (signal.type === 'connected') {
      this.setSnapshot({
        connected: true,
        state: this.snapshot.registered ? 'READY' : 'CONNECTING',
        error: '',
      });
      return;
    }
    if (signal.type === 'disconnected') {
      this.setSnapshot({
        connected: false,
        registered: false,
        state: this.session.hasSession() ? this.snapshot.state : 'CONNECTING',
        error: 'Соединение с АТС восстанавливается…',
      });
      this.emit('registration', { registered: false, reason: 'disconnected' });
      return;
    }
    if (signal.type === 'registered') {
      this.setSnapshot({
        connected: true,
        registered: true,
        state: this.session.hasSession() ? this.snapshot.state : 'READY',
        error: '',
      });
      this.emit('registration', { registered: true });
      return;
    }
    if (signal.type === 'unregistered') {
      this.setSnapshot({
        connected: this.registration.isConnected(),
        registered: false,
        state: this.session.hasSession() ? this.snapshot.state : 'CONNECTING',
        error: '',
      });
      this.emit('registration', { registered: false, reason: 'unregistered' });
      return;
    }

    this.setSnapshot({
      registered: false,
      state: this.session.hasSession() ? this.snapshot.state : 'ERROR',
      error: signal.reason,
    });
    this.emit('registration', { registered: false, reason: signal.reason });
  }

  private handleRegistrationSession(session: any, direction: 'INBOUND' | 'OUTBOUND'): void {
    if (this.destroyed) return;
    this.webBuildGate.sync();
    this.session.handleNewSession(session, direction);
  }

  async dial(value: string, options: DialOptions = {}): Promise<any> { return this.session.dial(value, options); }
  async answer(): Promise<void> { return this.session.answer(); }
  decline(): void { this.session.decline(); }
  hangup(): void { this.session.hangup(); }
  mute(): void { this.session.mute(); }
  unmute(): void { this.session.unmute(); }
  hold(): void { this.session.hold(); }
  unhold(): void { this.session.unhold(); }
  sendDtmf(tone: string): void { this.session.sendDtmf(tone); }

  async activateThisTab(): Promise<void> {
    if (this.webBuildGate.isPending()) {
      throw new Error('Доступна новая версия портала — обновите страницу');
    }
    if (!this.config?.configured || !this.config.account) {
      throw new Error(this.config?.unavailableReason || 'Телефон не настроен для пользователя');
    }
    if (this.session.hasSession()) {
      throw new Error('Нельзя переносить телефон во время активного звонка');
    }

    const config = this.config;
    const account = config.account!;
    this.tabLease.configureOwner(account.id || account.extension);
    if (!this.tabLease.takeOver()) {
      throw new Error('Не удалось активировать телефон в этом окне');
    }

    await this.configure(config);

    if (!this.snapshot.primaryTab) {
      throw new Error('Не удалось активировать телефон в этом окне');
    }
  }

  async requestMicrophone(): Promise<void> { await this.media.requestMicrophone(); }
  cancelPreparedMicrophone(): void { this.media.cancelPreparedMicrophone(this.session.hasSession()); }

  completePostProcessing(): void {
    if (this.session.hasSession()) return;
    this.setSnapshot({
      state: this.snapshot.registered ? 'READY' : 'OFFLINE',
      startedAt: null,
      endedAt: null,
      sessionDirection: null,
      remoteNumber: '',
      muted: false,
      held: false,
      error: '',
    });
  }

  restoreCallStartedAt(value: number | string | Date | null | undefined): void {
    if (!value) {
      this.setSnapshot({ startedAt: null, endedAt: null });
      return;
    }
    const timestamp = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(String(value));
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      this.setSnapshot({ startedAt: null, endedAt: null });
      return;
    }
    this.setSnapshot({ startedAt: timestamp, endedAt: null });
  }

  async stop(releaseLease = true): Promise<void> {
    this.destroyed = true;
    this.pendingConfig = null;

    this.session.prepareStop();
    this.registration.prepareStop();
    this.media.prepareStop();
    this.tabLease.stopTimers();

    this.session.finishStop();
    this.registration.finishStop();
    this.media.finishStop();

    if (releaseLease) this.tabLease.release();
    this.setSnapshot({ ...EMPTY_SNAPSHOT });
    this.emit('callActivity', { active: false });
  }

  private setSnapshot(patch: Partial<SipSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.emit('snapshot', this.getSnapshot());
  }

}

export const sipManager = SipManager.getInstance();
